import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGetData,
  type GetImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGetError,
  type GetImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGetResponse,
  getImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGet,
} from '../../generated';

export type GetImageScoresQueryVariables = GetImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGetData['path'];

export type GetImageScoresQuery = GetImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGetResponse;

export const useGetImageScoresQuery = <
  TData = GetImageScoresQuery,
  TError = GetImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGetError,
>(
  { portfolio_id, pdp_id }: GetImageScoresQueryVariables,
  options?: Omit<UseQueryOptions<GetImageScoresQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageScoresQuery, TError, TData>({
    queryKey: ['GetImageScores', { portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageScoresQuery;
    },
    ...options,
  });
};

useGetImageScoresQuery.getKey = ({ portfolio_id, pdp_id }: GetImageScoresQueryVariables) => [
  'GetImageScores',
  { portfolio_id, pdp_id },
];

useGetImageScoresQuery.fetcher = (
  { portfolio_id, pdp_id }: GetImageScoresQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageScoresV1PortfoliosPortfolioIdPdpsPdpIdImageScoresGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageScoresQuery;
  };
};

useGetImageScoresQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/image-scores',
};
